import React, { useEffect, useRef, useState } from "react";
import GoogleLoginComponent from "./components/Login/GoogleLoginComponent";
import { googleLogout } from "@react-oauth/google";
import './App.css';
import { EmployeeProvider } from './utils/EmpDetails/EmployeeProvider';
import SearchBar from './SearchBar/SearchBar';
import { AppProvider } from './context/Context';
import CallDetails from './components/CallDetails/CallDetails';
import { useLocation, useNavigate } from "react-router-dom";
import getIP from "./utils/EmpDetails/getIP";
import getCookie from "./utils/GetCookie/getCookie";
import { urlDomain } from "./config/constant";
import { translateVendorPhone } from "./services/piiService";

// Module-level: coalesces concurrent /protected calls into one request.
// Cleared after settling so a hard-refresh always hits the network.
let _protectedPromise = null;

function App() {
  const [user, setUser] = useState(undefined); // Start with undefined instead of null
  const [ip, setIp] = useState(null);
  const [loginMessage, setLoginMessage] = useState("")
  const navigate = useNavigate(); // Initialize useNavigate
  const apiDomain = urlDomain();
  const location = useLocation();
  const currentPath = location.pathname;
  // Redirect only when user is on entry pages
  const isEntryPage = ["/", "/login.php"].includes(currentPath) || currentPath.startsWith("/?msg=");

  // Resolves with this page's URL (path + search) once the vendor phone
  // translation below has settled — the translated URL if it ran, else the
  // URL as-is. previousPathUrl must only ever be saved from this, never from
  // the mount-time location, or the vendor-encrypted `phone` gets saved and
  // later restored without being translated again.
  const translatedUrlRef = useRef(null);
  if (!translatedUrlRef.current) {
    let resolve;
    const promise = new Promise((r) => { resolve = r; });
    translatedUrlRef.current = { promise, resolve };
  }
  const savePreviousPathUrl = () => {
    translatedUrlRef.current.promise.then((url) => {
      localStorage.setItem("previousPathUrl", url);
    });
  };

  useEffect(() => {
    if (_protectedPromise) return;

    _protectedPromise = fetch(`${apiDomain}/protected`, { credentials: "include" })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setUser(data.user);
          const currentPath = location.pathname;
          const currentSearch = location.search;


          if (!isEntryPage && currentPath !== "/api/data_dump") {
            savePreviousPathUrl();
          }

          if (isEntryPage) {
            const savedUrl = localStorage.getItem("previousPathUrl") || "";
            const isInvalidSavedUrl =
              savedUrl === "/" ||
              savedUrl === "/login.php" ||
              savedUrl.includes("msg=1") ||
              savedUrl.includes("msg=2") ||
              savedUrl === "";

            const defaultUrl = `/api/html?empid=${data.user.empid}`;

            navigate(isInvalidSavedUrl ? defaultUrl : savedUrl);
          }
        } else {
          if (!isEntryPage && currentPath !== "/api/data_dump") {
            savePreviousPathUrl();
          }
          setUser(null);
          if(location.search === ""){
            navigate("/");
          }else if (location.search === "?msg=1") {
            setLoginMessage("You have successfully logged out.");
            navigate("/?msg=1");
          } else if(location.search === "?msg=2") {
            setLoginMessage("Your Session Has Expired. Please Login Again.");
            navigate("/?msg=2");
          }else{
            setLoginMessage("Your Session Has Expired. Please Login Again.");
            navigate("/?msg=2");
          }
        }
      })
      .catch(() => {
        setUser(null);
        navigate("/?msg=2");
      })
      .finally(() => {
        _protectedPromise = null;
      });
  }, []);

  useEffect(() => {
    getIP()
      .then((ipResponse) => {
        const fetchedIp = ipResponse.data.ip || getCookie("CCIP");
        setIp(fetchedIp);
      })
      .catch((ipError) => {
        console.error("Error fetching IP address:", ipError);
      });
  }, []);

  // The very first inbound-call URL carries `phone` encrypted by the
  // external vendor/dialer's own key. Every other component (SearchBar,
  // EmployeeProvider, etc.) assumes `phone` is already in OUR key's
  // ciphertext, so this translates it — once, before anything else mounts —
  // and rewrites the address bar. `rsrch` is added to every URL SearchBar's
  // own changeURL() produces, so its absence is what marks this as the
  // vendor's original, untranslated link.
  const [phoneReady, setPhoneReady] = useState(false);
  // Guards against StrictMode's dev-only double-invoke of this effect (mount
  // -> cleanup -> remount) actually firing the translate call twice, which
  // would also produce two different ciphertexts for the same number (AES
  // uses a random IV per encryptPII call) and two competing navigate() calls.
  const didTranslatePhone = useRef(false);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const rawPhone = params.get('phone');
    if (!rawPhone || params.get('rsrch')) {
      translatedUrlRef.current.resolve(location.pathname + location.search);
      setPhoneReady(true);
      return;
    }
    if (didTranslatePhone.current) return;
    didTranslatePhone.current = true;
    let finalUrl = location.pathname + location.search;
    translateVendorPhone(rawPhone)
      .then((translated) => {
        if (translated) {
          params.set('phone', translated);
          // Mark this URL as already-translated so a page refresh from this
          // exact address bar doesn't try to run vendorKey decryption again
          // on what's now our own ciphertext.
          params.set('rsrch', '1');
          finalUrl = `${location.pathname}?${params.toString()}`;
          navigate(finalUrl, { replace: true });
        }
      })
      .finally(() => {
        translatedUrlRef.current.resolve(finalUrl);
        setPhoneReady(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    googleLogout();
    fetch(`${apiDomain}/auth/logout`, {
      method: "POST",
      credentials: "include",
    }).then(() => {
      setUser(null);
      navigate("/?msg=1");
      setLoginMessage("You have successfully logged out.");
    });
  };

  // Only render after user is either set or confirmed as null, and after
  // the vendor-encrypted phone param (if any) has been translated to ours.
  if (user === undefined || !phoneReady) {
    return null; // Or a blank screen while checking session
  }

    return (
      <div>
        {user ? (
          <EmployeeProvider>
            <AppProvider>
              {document.location.pathname === "/api/data_dump" ? (
                <CallDetails empid={user?.empid} ak="" />
              ) : (
                <SearchBar handleLogout={handleLogout} />
              )}
            </AppProvider>
          </EmployeeProvider>
        ) : (
          <GoogleLoginComponent onLoginSuccess={setUser} onLogout={handleLogout} ip={ip} loginMessage={loginMessage} setLoginMessage={setLoginMessage} />
        )}
      </div>
    );
  }

export default App;
